import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Archive as ArchiveIcon,
  Delete as DeleteIcon,
  OpenInNew as OpenIcon,
  Unarchive as UnarchiveIcon,
} from '@mui/icons-material';
import PageHeader from '../../components/ui/PageHeader';
import axios from '../../utils/axios';
import { notify } from '../../utils/notify';
import { resolveCanFinance } from './salesPlanMatrix';

const nf = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 });
const fmtInt = (n) => nf.format(Math.round(Number(n) || 0));
const fmtMoney = (n) => `${nf.format(Math.round(Number(n) || 0))} ₽`;

function fmtDate(s) {
  if (!s) return '—';
  const d = String(s).slice(0, 10).split('-');
  if (d.length !== 3) return String(s);
  return `${d[2]}.${d[1]}.${d[0]}`;
}

const STATUS_LABEL = { draft: 'Черновик', active: 'Активный', archived: 'Архивный' };
const STATUS_COLOR = { draft: 'default', active: 'success', archived: 'default' };

export default function SalesPlans() {
  const navigate = useNavigate();
  const user = useSelector((s) => s.auth.user);
  const [tab, setTab] = useState('active');
  const [plans, setPlans] = useState([]);
  const [apiCanFinance, setApiCanFinance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const canFinance = resolveCanFinance(apiCanFinance, user);
  const tableColSpan = 5 + (canFinance ? 2 : 0) + (tab === 'active' ? 1 : 0);

  const load = useCallback(async (status) => {
    setLoading(true);
    try {
      const resp = await axios.get('/analytics/plans', { params: { status } });
      setPlans(resp.data?.plans || []);
      setApiCanFinance(resp.data?.can_finance ?? null);
    } catch (_) {
      setPlans([]);
      setApiCanFinance(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(tab); }, [tab, load]);

  const handleArchiveToggle = async (plan) => {
    setBusyId(plan.id);
    try {
      const action = plan.status === 'archived' ? 'activate' : 'archive';
      await axios.post(`/analytics/plans/${plan.id}/${action}`);
      notify(plan.status === 'archived' ? 'План активирован' : 'План отправлен в архив', { severity: 'success' });
      await load(tab);
    } catch (_) {
      /* ошибка показана интерсептором */
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setBusyId(confirmDelete.id);
    try {
      await axios.delete(`/analytics/plans/${confirmDelete.id}`);
      notify('План удалён', { severity: 'success' });
      setConfirmDelete(null);
      await load(tab);
    } catch (_) {
      /* ошибка показана интерсептором */
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <PageHeader
        title="Планы продаж"
        subtitle="Планирование продаж по группам, подгруппам и товарам на основе прошлых периодов"
        actions={(
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/analytics/plans/new')}>
            Создать план
          </Button>
        )}
      />

      {!canFinance && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Финансовые показатели скрыты: доступен производственный вид плана только в штуках.
        </Typography>
      )}

      <Paper elevation={0} sx={{ border: (t) => `1px solid ${t.palette.outlineVariant}`, borderRadius: 3, overflow: 'hidden' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2, borderBottom: (t) => `1px solid ${t.palette.outlineVariant}` }}>
          <Tab value="active" label="Активные" />
          <Tab value="archived" label="Архивные" />
        </Tabs>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Название</TableCell>
                <TableCell>Период прогноза</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell align="right">План, шт</TableCell>
                {canFinance && <TableCell align="right">План, сумма</TableCell>}
                {canFinance && <TableCell align="right">План, профит</TableCell>}
                {tab === 'active' && <TableCell align="right">Выполнение</TableCell>}
                <TableCell align="right">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={tableColSpan} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              )}
              {!loading && plans.length === 0 && (
                <TableRow>
                  <TableCell colSpan={tableColSpan} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Планов нет
                  </TableCell>
                </TableRow>
              )}
              {!loading && plans.map((p) => {
                const completion = p.completion_pct;
                const completionColor = completion == null ? 'default'
                  : completion >= 100 ? 'success'
                    : completion >= 70 ? 'warning' : 'error';
                return (
                  <TableRow
                    key={p.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/analytics/plans/${p.id}`)}
                  >
                    <TableCell sx={{ fontWeight: 500 }}>{p.name}</TableCell>
                    <TableCell>{fmtDate(p.plan_from)} – {fmtDate(p.plan_to)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={STATUS_LABEL[p.status] || p.status} color={STATUS_COLOR[p.status] || 'default'} variant="outlined" />
                    </TableCell>
                    <TableCell align="right">{fmtInt(p.plan?.qty)}</TableCell>
                    {canFinance && <TableCell align="right">{fmtMoney(p.plan?.revenue)}</TableCell>}
                    {canFinance && <TableCell align="right">{fmtMoney(p.plan?.profit)}</TableCell>}
                    {tab === 'active' && (
                      <TableCell align="right">
                        {completion == null ? '—' : (
                          <Chip
                            size="small"
                            label={`${fmtInt(completion)}%`}
                            color={completionColor}
                            variant={completionColor === 'default' ? 'outlined' : 'filled'}
                          />
                        )}
                      </TableCell>
                    )}
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Открыть">
                        <IconButton size="small" onClick={() => navigate(`/analytics/plans/${p.id}`)}>
                          <OpenIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={p.status === 'archived' ? 'Активировать' : 'В архив'}>
                        <span>
                          <IconButton size="small" disabled={busyId === p.id} onClick={() => handleArchiveToggle(p)}>
                            {p.status === 'archived' ? <UnarchiveIcon fontSize="small" /> : <ArchiveIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Удалить">
                        <span>
                          <IconButton size="small" color="error" disabled={busyId === p.id} onClick={() => setConfirmDelete(p)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Удалить план?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            План «{confirmDelete?.name}» и все его позиции будут удалены безвозвратно.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Отмена</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={busyId === confirmDelete?.id}>
            Удалить
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
